/**
 * OpportunityContacts - HubSpot Style Customer Contacts Management
 * Manages customer contacts for opportunities
 */
import React, { useState, useEffect } from 'react';
import {
  User, Mail, Phone, Building2, Star, Crown, Plus, Edit2, Trash2,
  MoreHorizontal, Linkedin, Calendar, MessageSquare, CheckCircle,
  UserCircle, Briefcase, X
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Contact role options
const CONTACT_ROLES = [
  { value: 'technical', label: 'Technical', icon: Briefcase },
  { value: 'commercial', label: 'Commercial', icon: Building2 },
  { value: 'executive', label: 'Executive', icon: Crown },
  { value: 'procurement', label: 'Procurement', icon: Briefcase },
  { value: 'engineering', label: 'Engineering', icon: Briefcase },
  { value: 'quality', label: 'Quality', icon: CheckCircle },
  { value: 'other', label: 'Other', icon: User },
];

// Contact method options
const CONTACT_METHODS = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'meeting', label: 'In Person' },
  { value: 'video_call', label: 'Video Call' },
  { value: 'other', label: 'Other' },
];

const OpportunityContacts = ({ opportunityId }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    contact_name: '',
    contact_title: '',
    contact_email: '',
    contact_phone: '',
    department: '',
    is_primary_contact: false,
    is_decision_maker: false,
    contact_role: 'technical',
    preferred_contact_method: 'email',
    last_contact_date: '',
    linkedin_url: '',
    notes: ''
  });

  useEffect(() => {
    if (opportunityId) {
      loadContacts();
    }
  }, [opportunityId]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/v1/pipeline/opportunities/${opportunityId}/contacts`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        }
      );
      const data = await response.json();
      if (data.success) {
        setContacts(data.data);
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.contact_name.trim()) {
      alert('Contact name is required');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const url = editingContact
        ? `${API_URL}/api/v1/pipeline/opportunities/${opportunityId}/contacts/${editingContact.id}`
        : `${API_URL}/api/v1/pipeline/opportunities/${opportunityId}/contacts`;
      
      const response = await fetch(url, {
        method: editingContact ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        await loadContacts();
        handleCloseDialog();
      } else {
        alert('Failed to save contact');
      }
    } catch (error) {
      console.error('Error saving contact:', error);
      alert('Failed to save contact');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/v1/pipeline/opportunities/${opportunityId}/contacts/${contactId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        }
      );
      
      if (response.ok) {
        await loadContacts();
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
    }
  };

  const handleSetPrimary = async (contactId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/v1/pipeline/opportunities/${opportunityId}/contacts/${contactId}/set-primary`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        }
      );
      
      if (response.ok) {
        await loadContacts();
      }
    } catch (error) {
      console.error('Error setting primary contact:', error);
    }
  };

  const handleOpenAddDialog = () => {
    setEditingContact(null);
    setFormData({
      contact_name: '',
      contact_title: '',
      contact_email: '',
      contact_phone: '',
      department: '',
      is_primary_contact: contacts.length === 0, // First contact is primary by default
      is_decision_maker: false,
      contact_role: 'technical',
      preferred_contact_method: 'email',
      last_contact_date: '',
      linkedin_url: '',
      notes: ''
    });
    setShowAddDialog(true);
  };

  const handleOpenEditDialog = (contact) => {
    setEditingContact(contact);
    setFormData({
      contact_name: contact.contact_name || '',
      contact_title: contact.contact_title || '',
      contact_email: contact.contact_email || '',
      contact_phone: contact.contact_phone || '',
      department: contact.department || '',
      is_primary_contact: contact.is_primary_contact || false,
      is_decision_maker: contact.is_decision_maker || false,
      contact_role: contact.contact_role || 'technical',
      preferred_contact_method: contact.preferred_contact_method || 'email',
      last_contact_date: contact.last_contact_date || '',
      linkedin_url: contact.linkedin_url || '',
      notes: contact.notes || ''
    });
    setShowAddDialog(true);
  };

  const handleCloseDialog = () => {
    setShowAddDialog(false);
    setEditingContact(null);
    setFormData({
      contact_name: '',
      contact_title: '',
      contact_email: '',
      contact_phone: '',
      department: '',
      is_primary_contact: false,
      is_decision_maker: false,
      contact_role: 'technical',
      preferred_contact_method: 'email',
      last_contact_date: '',
      linkedin_url: '',
      notes: ''
    });
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'executive': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'commercial': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'technical': return 'bg-green-100 text-green-700 border-green-200';
      case 'procurement': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'engineering': return 'bg-cyan-100 text-cyan-700 border-cyan-200';
      case 'quality': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-slate-500">Loading contacts...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Customer Contacts</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {contacts.length} contact{contacts.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button 
          size="sm" 
          onClick={handleOpenAddDialog}
          className="gap-1"
        >
          <Plus className="w-4 h-4" />
          Add Contact
        </Button>
      </div>

      {/* Contacts Grid */}
      {contacts.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <UserCircle className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <h4 className="text-sm font-medium text-slate-900 mb-1">No contacts yet</h4>
            <p className="text-xs text-slate-500 mb-4">
              Add customer contacts to keep track of key stakeholders
            </p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleOpenAddDialog}
              className="gap-1"
            >
              <Plus className="w-4 h-4" />
              Add First Contact
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {contacts.map((contact) => (
            <Card 
              key={contact.id} 
              className={`transition-all hover:shadow-md ${contact.is_primary_contact ? 'ring-2 ring-blue-200 border-blue-300' : ''}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {/* Avatar */}
                  <div className="relative">
                    <Avatar className="w-12 h-12">
                      <AvatarFallback className={`text-sm font-medium ${contact.is_primary_contact ? 'bg-blue-500 text-white' : 'bg-slate-200 text-slate-700'}`}>
                        {getInitials(contact.contact_name)}
                      </AvatarFallback>
                    </Avatar>
                    {contact.is_primary_contact && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <Star className="w-3 h-3 text-white fill-white" />
                      </div>
                    )}
                  </div>

                  {/* Contact Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-semibold text-slate-900">
                            {contact.contact_name}
                          </h4>
                          {contact.is_decision_maker && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <Crown className="w-4 h-4 text-amber-500" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-xs">Decision Maker</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                        {contact.contact_title && (
                          <p className="text-xs text-slate-600">{contact.contact_title}</p>
                        )}
                        {contact.department && (
                          <p className="text-xs text-slate-500">{contact.department}</p>
                        )}
                      </div>

                      {/* Actions */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleOpenEditDialog(contact)}>
                            <Edit2 className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          {!contact.is_primary_contact && (
                            <DropdownMenuItem onClick={() => handleSetPrimary(contact.id)}>
                              <Star className="w-4 h-4 mr-2" />
                              Set as Primary
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleDelete(contact.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    {/* Contact Details */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
                      {contact.contact_email && (
                        <a 
                          href={`mailto:${contact.contact_email}`}
                          className="flex items-center gap-1 text-xs text-slate-600 hover:text-blue-600"
                        >
                          <Mail className="w-3 h-3" />
                          {contact.contact_email}
                        </a>
                      )}
                      {contact.contact_phone && (
                        <a 
                          href={`tel:${contact.contact_phone}`}
                          className="flex items-center gap-1 text-xs text-slate-600 hover:text-blue-600"
                        >
                          <Phone className="w-3 h-3" />
                          {contact.contact_phone}
                        </a>
                      )}
                      {contact.linkedin_url && (
                        <a 
                          href={contact.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-slate-600 hover:text-blue-600"
                        >
                          <Linkedin className="w-3 h-3" />
                          LinkedIn
                        </a>
                      )}
                    </div>

                    {/* Tags & Meta */}
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      <Badge variant="outline" className={`text-xs ${getRoleColor(contact.contact_role)}`}>
                        {CONTACT_ROLES.find(r => r.value === contact.contact_role)?.label || contact.contact_role}
                      </Badge>
                      {contact.last_contact_date && (
                        <span className="flex items-center gap-1 text-xs text-slate-500">
                          <Calendar className="w-3 h-3" />
                          Last contact: {formatDate(contact.last_contact_date)}
                        </span>
                      )}
                    </div>

                    {/* Notes */}
                    {contact.notes && (
                      <div className="mt-3 p-2 bg-slate-50 rounded-md">
                        <p className="text-xs text-slate-600 line-clamp-2">{contact.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Contact Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingContact ? 'Edit Contact' : 'Add New Contact'}
            </DialogTitle>
            <DialogDescription>
              {editingContact 
                ? 'Update contact information' 
                : 'Add a customer contact for this opportunity'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Name & Title */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <Input
                  value={formData.contact_name}
                  onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Title</label>
                <Input
                  value={formData.contact_title}
                  onChange={(e) => setFormData({...formData, contact_title: e.target.value})}
                  placeholder="Senior Buyer"
                />
              </div>
            </div>

            {/* Email & Phone */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Email</label>
                <Input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                  placeholder="john@company.com"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Phone</label>
                <Input
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                  placeholder="+1 555-123-4567"
                />
              </div>
            </div>

            {/* Department & Role */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Department</label>
                <Input
                  value={formData.department}
                  onChange={(e) => setFormData({...formData, department: e.target.value})}
                  placeholder="Purchasing"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Role Type</label>
                <Select 
                  value={formData.contact_role}
                  onValueChange={(value) => setFormData({...formData, contact_role: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTACT_ROLES.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Contact Preferences */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Preferred Contact Method</label>
                <Select 
                  value={formData.preferred_contact_method}
                  onValueChange={(value) => setFormData({...formData, preferred_contact_method: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTACT_METHODS.map((method) => (
                      <SelectItem key={method.value} value={method.value}>
                        {method.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Last Contact Date</label>
                <Input
                  type="date"
                  value={formData.last_contact_date}
                  onChange={(e) => setFormData({...formData, last_contact_date: e.target.value})}
                />
              </div>
            </div>

            {/* LinkedIn */}
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">LinkedIn URL</label>
              <Input
                value={formData.linkedin_url}
                onChange={(e) => setFormData({...formData, linkedin_url: e.target.value})}
                placeholder="https://linkedin.com/in/johnsmith"
              />
            </div>

            {/* Flags */}
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_primary_contact}
                  onChange={(e) => setFormData({...formData, is_primary_contact: e.target.checked})}
                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-slate-700">Primary Contact</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_decision_maker}
                  onChange={(e) => setFormData({...formData, is_decision_maker: e.target.checked})}
                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-slate-700">Decision Maker</span>
              </label>
            </div>

            {/* Notes */}
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Additional notes about this contact..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : (editingContact ? 'Update Contact' : 'Add Contact')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OpportunityContacts;
